import { z } from "zod/v4";

export const clubTournamentDisciplineSchema = z.enum([
	"beach",
	"indoor",
	"padel",
	"tennis",
	"pickleball",
]);

export const clubTournamentFormatSchema = z.enum([
	"single_elim",
	"double_elim",
	"round_robin",
	"pool_knockout",
	"swiss",
]);

export const clubTournamentStatusSchema = z.enum([
	"draft",
	"registration",
	"seeding",
	"ready",
	"in_progress",
	"completed",
	"cancelled",
]);

export const clubTournamentVisibilitySchema = z.enum(["draft", "club_members", "app", "public"]);

export const clubTournamentSeedingMethodSchema = z.enum([
	"rating",
	"random",
	"manual",
	"registration_order",
]);

export const clubTournamentTiebreakerSchema = z.enum([
	"head_to_head",
	"set_differential",
	"point_differential",
	"sets_won",
	"points_for",
	"random",
]);

export const clubTournamentRatingEligibilitySchema = z.enum(["rated", "unrated", "partial"]);

export const clubTournamentTeamStatusSchema = z.enum([
	"registered",
	"checked_in",
	"withdrawn",
	"eliminated",
	"disqualified",
]);

export const clubTournamentMemberRoleSchema = z.enum(["captain", "player", "substitute"]);

export const clubTournamentNodeKindSchema = z.enum(["match", "pool_standing"]);

export const clubTournamentNodeStatusSchema = z.enum([
	"pending",
	"ready",
	"in_progress",
	"completed",
	"bye",
	"walkover",
]);

export const clubTournamentOutcomeReasonSchema = z.enum([
	"normal",
	"forfeit",
	"disqualification",
	"bye",
	"walkover",
]);

export const clubTournamentSchema = z.object({
	id: z.string().uuid(),
	club_id: z.string().uuid().nullable(),
	creator_id: z.string().uuid(),
	title: z.string(),
	slug: z.string().nullable(),
	description: z.string(),
	discipline: clubTournamentDisciplineSchema,
	format: clubTournamentFormatSchema,
	status: clubTournamentStatusSchema,
	visibility: clubTournamentVisibilitySchema,
	team_size: z.number().int(),
	min_teams: z.number().int(),
	max_teams: z.number().int(),
	entry_fee_cents: z.number().int().nullable(),
	currency: z.string().nullable(),
	registration_opens_at: z.coerce.date().nullable(),
	registration_closes_at: z.coerce.date().nullable(),
	start_at: z.coerce.date(),
	end_at: z.coerce.date().nullable(),
	location_id: z.string().uuid().nullable(),
	seeding_method: clubTournamentSeedingMethodSchema,
	tiebreaker_rules: z.array(clubTournamentTiebreakerSchema),
	rating_eligibility: clubTournamentRatingEligibilitySchema,
	forfeit_after_minutes: z.number().int(),
	min_rest_minutes: z.number().int(),
	dispute_window_hours: z.number().int(),
	completed_at: z.coerce.date().nullable(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
});

// A team member's linked DVV (federation) identity, set by an organizer for the
// results export. license_number is nullable (not every athlete has one on file).
export const clubTournamentDvvLinkSchema = z.object({
	scraped_athlete_id: z.string().uuid(),
	athlete_name: z.string(),
	athlete_slug: z.string(),
	federation: z.string(),
	license_number: z.string().nullable(),
});

export const clubTournamentTeamMemberSchema = z.object({
	id: z.string().uuid(),
	user_id: z.string().uuid(),
	role: clubTournamentMemberRoleSchema,
	display_name: z.string().nullish(),
	dvv: clubTournamentDvvLinkSchema.nullish(),
});

export const clubTournamentTeamSchema = z.object({
	id: z.string().uuid(),
	tournament_id: z.string().uuid(),
	name: z.string(),
	status: clubTournamentTeamStatusSchema,
	seed: z.number().int().nullable(),
	rating_snapshot: z.number().nullable(),
	registered_at: z.coerce.date(),
	checked_in_at: z.coerce.date().nullable(),
	withdrawn_at: z.coerce.date().nullable(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
	members: z.array(clubTournamentTeamMemberSchema).default([]),
});

export const clubTournamentBracketSchema = z.object({
	id: z.string().uuid(),
	tournament_id: z.string().uuid(),
	kind: clubTournamentFormatSchema,
	rounds_count: z.number().int(),
	generated_at: z.coerce.date(),
	created_at: z.coerce.date(),
});

export const clubTournamentNodeSchema = z.object({
	id: z.string().uuid(),
	tournament_id: z.string().uuid(),
	bracket_id: z.string().uuid(),
	kind: clubTournamentNodeKindSchema,
	round: z.number().int(),
	position_in_round: z.number().int(),
	status: clubTournamentNodeStatusSchema,
	home_team_id: z.string().uuid().nullable(),
	away_team_id: z.string().uuid().nullable(),
	winner_team_id: z.string().uuid().nullable(),
	match_id: z.string().uuid().nullable(),
	next_node_id: z.string().uuid().nullable(),
	loser_next_node_id: z.string().uuid().nullable(),
	court_id: z.string().uuid().nullable(),
	scheduled_at: z.coerce.date().nullable(),
	started_at: z.coerce.date().nullable(),
	completed_at: z.coerce.date().nullable(),
	outcome_reason: clubTournamentOutcomeReasonSchema.nullable(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
});

export const clubTournamentStandingSchema = z.object({
	id: z.string().uuid(),
	tournament_id: z.string().uuid(),
	team_id: z.string().uuid(),
	placement: z.number().int(),
	matches_played: z.number().int(),
	matches_won: z.number().int(),
	sets_won: z.number().int(),
	sets_lost: z.number().int(),
	points_for: z.number().int(),
	points_against: z.number().int(),
	tiebreaker_applied: z.string().nullable(),
	snapshot_at: z.coerce.date(),
});

export const createClubTournamentSchema = z.object({
	club_id: z.string().uuid().nullable().optional(),
	title: z.string().min(1),
	description: z.string(),
	discipline: clubTournamentDisciplineSchema,
	format: clubTournamentFormatSchema,
	team_size: z.number().int().positive(),
	min_teams: z.number().int().min(2),
	max_teams: z.number().int().min(2),
	entry_fee_cents: z.number().int().min(0).nullable().optional(),
	currency: z.string().nullable().optional(),
	registration_opens_at: z.coerce.date().nullable().optional(),
	registration_closes_at: z.coerce.date().nullable().optional(),
	start_at: z.coerce.date(),
	end_at: z.coerce.date().nullable().optional(),
	location_id: z.string().uuid().nullable().optional(),
	seeding_method: clubTournamentSeedingMethodSchema,
	tiebreaker_rules: z.array(clubTournamentTiebreakerSchema),
	rating_eligibility: clubTournamentRatingEligibilitySchema,
});

export const updateClubTournamentSchema = z.object({
	title: z.string().min(1).optional(),
	description: z.string().optional(),
	start_at: z.coerce.date().optional(),
	end_at: z.coerce.date().nullable().optional(),
	location_id: z.string().uuid().nullable().optional(),
	max_teams: z.number().int().min(2).optional(),
	entry_fee_cents: z.number().int().min(0).nullable().optional(),
	registration_opens_at: z.coerce.date().nullable().optional(),
	registration_closes_at: z.coerce.date().nullable().optional(),
	tiebreaker_rules: z.array(clubTournamentTiebreakerSchema).optional(),
	rating_eligibility: clubTournamentRatingEligibilitySchema.optional(),
	forfeit_after_minutes: z.number().int().positive().optional(),
	min_rest_minutes: z.number().int().min(0).optional(),
	dispute_window_hours: z.number().int().min(0).optional(),
});

export const setClubTournamentVisibilitySchema = z.object({
	visibility: clubTournamentVisibilitySchema,
	slug: z.string().nullable().optional(),
});

export const registerClubTournamentTeamSchema = z.object({
	name: z.string().min(1),
	members: z.array(z.string().uuid()).min(1),
	captain: z.string().uuid(),
	roles: z.array(clubTournamentMemberRoleSchema).optional(),
});

export const setClubTournamentMemberLicenseSchema = z.object({
	scraped_athlete_id: z.string().uuid(),
});

export const seedClubTournamentSchema = z.object({
	method: clubTournamentSeedingMethodSchema,
	overrides: z.record(z.string().uuid(), z.number().int()).optional(),
});

export const markClubTournamentWalkoverSchema = z.object({
	winner_team_id: z.string().uuid(),
	outcome_reason: clubTournamentOutcomeReasonSchema,
});

export const clubTournamentDetailResponseSchema = z.object({
	tournament: clubTournamentSchema,
	teams: z.array(clubTournamentTeamSchema),
	bracket: clubTournamentBracketSchema.nullish(),
	nodes: z.array(clubTournamentNodeSchema).optional(),
	standings: z.array(clubTournamentStandingSchema).optional(),
});

export const clubTournamentListResponseSchema = z.object({
	items: z.array(clubTournamentSchema),
	total: z.number().int(),
});
